import { dataQualityIssuesResponseSchema, dataQualityOwnersResponseSchema } from './workspace.js';
import { resourcesResponseSchema, resourceProfileResponseSchema } from './resources.js';
import { tempoTeamsResponseSchema, resourceSourcesSchema, tempoAbsenceMappingSchema, tempoWorklogAbsencesSettingsResponseSchema, } from './integrations.js';
import { exchangeRateRangeSchema, exchangeRateCoverageResponseSchema, exchangeRateResyncResponseSchema, } from './exchange-rates.js';
import { z } from 'zod';
import { agentMessageInputSchema } from './agent.js';
import { allocationDeletionsResponseSchema, allocationRestoreResponseSchema, allocationInputSchema, allocationDeleteInputSchema, allocationUpdateSchema, capacityValueRateInputSchema, capacityValueRateSchema, capacityCalendarSchema, capacityOverviewSchema, capacityPointSchema, dailyAllocationInputSchema, dailyAllocationStaffingInputSchema, dailyAllocationUpdateSchema, dailyAllocationBulkDeleteSchema, dailyAllocationRescheduleSchema, expertiseSchema, } from './capacity.js';
import { budgetDashboardResponseSchema, budgetPhaseInputSchema, budgetPhaseUpdateSchema, budgetPhaseMappingInputSchema, budgetVersionSchema, budgetEnvelopeInputSchema, budgetEnvelopeRestoreInputSchema, budgetEnvelopeUpdateInputSchema, budgetSummarySchema, } from './budgets.js';
import { budgetImportInputSchema, budgetImportRunSchema } from './budget-imports.js';
import { blockedWorklogsSummaryResponseSchema, billingPeriodPreviewSchema, billingValidationDecisionSchema, billingValidationGroupDecisionSchema, billingValidationGroupSchema, createPeriodBillingBatchSchema, depositApplicationInputSchema, projectDepositInputSchema, projectDepositSummarySchema, projectBillingSettingsInputSchema, projectBillingSettingsSchema, } from './billing.js';
import { decisionInputSchema, noteInputSchema, publicationSnapshotSchema, reportBaselineSchema, reportProjectSchema, riskInputSchema, } from './governance.js';
import { clientTrackingInputSchema, projectContractStatusSchema, projectSchema, projectSignatureInputSchema, projectSoldAverageRateOverrideInputSchema, projectTrackingInputSchema, portfolioInsightsSchema, } from './portfolio.js';
import { planningSettingsSchema } from './planning.js';
import { financialForecastSchema, canonicalRateImportInputSchema, projectForecastSettingsInputSchema, projectInsightSchema, standardRateInputSchema, } from './forecast.js';
import { moneySchema } from './common.js';
import { contractSettingsSchema, projectContractInputSchema, projectContractSummarySchema, projectHistoryEntrySchema, projectStateInputSchema, projectStateResponseSchema, } from './project-contracts.js';
import { squadPlanningPeriodsResponseSchema, squadsResponseSchema, squadOptionsResponseSchema, squadCreateSchema, squadAdminUpdateSchema, squadMembershipUpdateSchema, squadSprintInputSchema, squadSprintUpdateSchema, squadSeedInputSchema, } from './squads.js';
const jsonBody = (schema) => ({
    required: true,
    content: { 'application/json': { schema: { $ref: `#/components/schemas/${schema}` } } },
});
const pathId = (name) => ({
    name,
    in: 'path',
    required: true,
    schema: { type: 'string', format: 'uuid' },
});
const jsonResponse = (schema) => ({
    description: 'OK',
    content: { 'application/json': { schema: { $ref: `#/components/schemas/${schema}` } } },
});
const blockedWorklogFilters = [
    { name: 'projectId', in: 'query', schema: { type: 'string', format: 'uuid' } },
    { name: 'clientId', in: 'query', schema: { type: 'string', format: 'uuid' } },
    { name: 'currency', in: 'query', schema: { type: 'string', enum: ['CAD', 'USD'] } },
    { name: 'from', in: 'query', schema: { type: 'string', format: 'date' } },
    { name: 'to', in: 'query', schema: { type: 'string', format: 'date' } },
    {
        name: 'status',
        in: 'query',
        schema: {
            type: 'string',
            enum: ['pending', 'approved', 'flagged', 'deferred', 'excluded', 'credited', 'invoiced'],
        },
    },
];
const scopeQueryParameter = {
    name: 'scope',
    in: 'query',
    required: false,
    description: "Use mine (default) for the viewer's squads or all when the viewer has portfolio.projects.read_all.",
    schema: { type: 'string', enum: ['mine', 'all'], default: 'mine' },
};
const projectLifecycleQueryParameter = {
    name: 'lifecycle',
    in: 'query',
    required: false,
    description: 'Use open (default) for tracked Tempo OPEN projects or all for every accessible project.',
    schema: { type: 'string', enum: ['open', 'all'], default: 'open' },
};
export const openApiDocument = {
    openapi: '3.1.0',
    info: { title: 'Pulse API', version: '1.0.0' },
    paths: {
        '/api/v1/capacity/allocation-deletions': {
            get: {
                summary: 'Read scoped deletion history with exact restore availability',
                parameters: [
                    { name: 'page', in: 'query', schema: { type: 'integer', minimum: 1 } },
                    { name: 'pageSize', in: 'query', schema: { type: 'integer', minimum: 1, maximum: 100 } },
                    { name: 'projectId', in: 'query', schema: { type: 'string', format: 'uuid' } },
                ],
                responses: { '200': jsonResponse('AllocationDeletionsResponse') },
            },
        },
        '/api/v1/capacity/allocation-deletions/{id}/restore': {
            post: {
                summary: 'Restore a deletion transactionally and idempotently',
                parameters: [pathId('id')],
                responses: {
                    '200': jsonResponse('AllocationRestoreResponse'),
                    '409': { description: 'Allocation restore conflict' },
                },
            },
        },
        '/api/v1/squads/planning-periods': {
            get: {
                summary: 'Read five periods from the shared organization sprint calendar',
                parameters: [{ name: 'start', in: 'query', schema: { type: 'string', format: 'date' } }],
                responses: { '200': jsonResponse('SquadPlanningPeriodsResponse') },
            },
        },
        '/api/v1/billing/time-entries/blocked-summary': {
            get: {
                summary: 'Count all blocked worklogs using the same effective-rate rules as the filtered list',
                parameters: blockedWorklogFilters,
                responses: { '200': jsonResponse('BlockedWorklogsSummaryResponse') },
            },
        },
        '/api/v1/settings/tempo-absences': {
            get: {
                summary: 'Read Timesheets absence source configuration and last successful reconciliation',
                responses: {
                    '200': jsonResponse('TempoWorklogAbsencesSettingsResponse'),
                    '403': { description: 'Requires global settings.read' },
                },
            },
        },
        '/api/v1/settings/rate-projects': {
            get: {
                summary: 'Read accessible rate projects with per-project management permission',
                responses: { '200': { description: 'Accessible projects and canManage flag' } },
            },
        },
        '/api/v1/settings/rate-expertises': {
            get: {
                summary: 'Read rate expertise options with financial.rates.read',
                responses: { '200': { description: 'Expertise options' } },
            },
        },
        '/api/v1/workspace/data-quality': {
            get: {
                summary: 'Read scoped anomalies and authorized correction destinations',
                responses: { '200': jsonResponse('DataQualityIssuesResponse') },
            },
        },
        '/api/v1/workspace/data-quality/owners': {
            get: {
                summary: 'Read distinct eligible active managers and administrators',
                responses: { '200': jsonResponse('DataQualityOwnersResponse') },
            },
        },
        '/api/v1/squads': {
            get: {
                summary: 'Visible PULSE business squads, without granting access-team membership',
                responses: { '200': jsonResponse('SquadsResponse') },
            },
            post: {
                summary: 'Create a business squad (settings.manage)',
                requestBody: jsonBody('SquadCreate'),
                responses: { '201': { description: 'Created' }, '403': { description: 'Forbidden' } },
            },
        },
        '/api/v1/squads/options': {
            get: {
                summary: 'Authorized resources/projects and administrative options',
                responses: { '200': jsonResponse('SquadOptionsResponse') },
            },
        },
        '/api/v1/squads/seed-from-tempo': {
            post: {
                summary: 'Initialize missing business squads from Tempo idempotently; never overwrite PULSE customizations',
                requestBody: jsonBody('SquadSeedInput'),
                responses: { '200': { description: 'Initialized' }, '403': { description: 'Forbidden' } },
            },
        },
        '/api/v1/squads/{squadId}': {
            patch: {
                summary: 'Update squad name, manager or archive state (admin only)',
                parameters: [pathId('squadId')],
                requestBody: jsonBody('SquadAdminUpdate'),
                responses: { '200': { description: 'Updated' }, '409': { description: 'Stale version' } },
            },
        },
        '/api/v1/squads/{squadId}/memberships': {
            put: {
                summary: 'Replace authorized squad resources/projects as assigned manager or admin',
                parameters: [pathId('squadId')],
                requestBody: jsonBody('SquadMembershipUpdate'),
                responses: { '200': { description: 'Updated' }, '409': { description: 'Stale version' } },
            },
        },
        '/api/v1/squads/{squadId}/sprints': {
            post: {
                summary: 'Create a local planning sprint using existing PULSE reservations',
                parameters: [pathId('squadId')],
                requestBody: jsonBody('SquadSprintInput'),
                responses: {
                    '201': { description: 'Created' },
                    '409': { description: 'Overlapping sprint' },
                },
            },
        },
        '/api/v1/squads/{squadId}/sprints/{sprintId}': {
            patch: {
                summary: 'Update a local sprint; updatedAt is required for concurrency',
                requestBody: jsonBody('SquadSprintUpdate'),
                parameters: [pathId('squadId'), pathId('sprintId')],
                responses: { '200': { description: 'Updated' }, '409': { description: 'Conflict' } },
            },
        },
        '/api/v1/projects/{projectId}/state': {
            patch: {
                summary: 'Update audited local status/signature or motivated production override; null restores automatic source',
                parameters: [pathId('projectId')],
                requestBody: jsonBody('ProjectStateInput'),
                responses: {
                    '200': jsonResponse('ProjectStateResponse'),
                    '409': { description: 'Project changed concurrently' },
                },
            },
        },
        '/api/v1/resources': {
            get: {
                summary: 'Resource directory constrained by capacity.read',
                responses: {
                    '200': {
                        description: 'OK',
                        content: {
                            'application/json': { schema: { $ref: '#/components/schemas/ResourcesResponse' } },
                        },
                    },
                    '403': { description: 'Forbidden' },
                },
            },
        },
        '/api/v1/resources/{resourceId}': {
            get: {
                summary: 'Resource portrait with workforce permission and project-filtered assigned tickets',
                responses: {
                    '200': {
                        description: 'OK',
                        content: {
                            'application/json': {
                                schema: { $ref: '#/components/schemas/ResourceProfileResponse' },
                            },
                        },
                    },
                    '403': { description: 'Forbidden' },
                },
            },
        },
        '/api/v1/admin/access/tempo-teams': {
            get: {
                summary: 'Read-only Tempo teams; local access scopes remain separate',
                responses: {
                    '200': {
                        description: 'OK',
                        content: {
                            'application/json': { schema: { $ref: '#/components/schemas/TempoTeamsResponse' } },
                        },
                    },
                    '403': { description: 'Forbidden' },
                },
            },
        },
        '/api/v1/integrations/freshness': {
            get: {
                summary: 'Jira and Tempo last success and freshness after two hours',
                responses: {
                    '200': {
                        description: 'OK',
                        content: {
                            'application/json': {
                                schema: {
                                    type: 'object',
                                    required: ['data'],
                                    properties: { data: { $ref: '#/components/schemas/ResourceSources' } },
                                },
                            },
                        },
                    },
                    '403': { description: 'Forbidden' },
                },
            },
        },
        '/api/v1/settings/exchange-rates': {
            get: {
                summary: 'USD/CAD observed coverage for a bounded period',
                responses: {
                    '200': {
                        description: 'OK',
                        content: {
                            'application/json': {
                                schema: { $ref: '#/components/schemas/ExchangeRateCoverageResponse' },
                            },
                        },
                    },
                    '403': { description: 'Forbidden' },
                },
            },
        },
        '/api/v1/settings/exchange-rates/resync': {
            post: {
                summary: 'Queue audited USD/CAD historical resynchronization (integrations.sync)',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': { schema: { $ref: '#/components/schemas/ExchangeRateRange' } },
                    },
                },
                responses: {
                    '202': {
                        description: 'Queued',
                        content: {
                            'application/json': {
                                schema: { $ref: '#/components/schemas/ExchangeRateResyncResponse' },
                            },
                        },
                    },
                    '409': { description: 'Another range is active' },
                },
            },
        },
        '/api/v1/health': {
            get: { summary: 'Service health', responses: { '200': { description: 'OK' } } },
        },
        '/api/v1/auth/session': {
            get: {
                summary: 'Authenticated user and effective permission summary',
                responses: { '200': { description: 'Session state' } },
            },
        },
        '/api/v1/admin/access/permissions': {
            get: {
                summary: 'Versioned permission catalog',
                responses: { '200': { description: 'Permission catalog' } },
            },
        },
        '/api/v1/admin/access/roles': {
            get: {
                summary: 'List roles and permissions',
                responses: { '200': { description: 'Roles' } },
            },
            post: {
                summary: 'Create a custom role',
                responses: { '201': { description: 'Created' }, '403': { description: 'Forbidden' } },
            },
        },
        '/api/v1/admin/access/roles/{id}': {
            patch: {
                summary: 'Update a role and its permission matrix',
                responses: { '200': { description: 'Updated' }, '409': { description: 'Protected role' } },
            },
        },
        '/api/v1/admin/access/users': {
            get: {
                summary: 'List provisioned users and role assignments',
                responses: { '200': { description: 'Users' } },
            },
            post: {
                summary: 'Pre-provision a Novatize user',
                responses: { '201': { description: 'Created' }, '409': { description: 'Conflict' } },
            },
        },
        '/api/v1/admin/access/users/{id}': {
            patch: {
                summary: 'Update a user and global roles',
                responses: { '200': { description: 'Updated' }, '409': { description: 'Last Admin' } },
            },
        },
        '/api/v1/admin/access/teams': {
            get: {
                summary: 'List teams, members, roles, and projects',
                responses: { '200': { description: 'Teams' } },
            },
            post: {
                summary: 'Create a multi-project team',
                responses: { '201': { description: 'Created' } },
            },
        },
        '/api/v1/admin/access/teams/{id}': {
            patch: {
                summary: 'Update team members, roles, and projects',
                responses: { '200': { description: 'Updated' } },
            },
        },
        '/api/v1/capacity': {
            get: {
                summary: 'Capacity timeline',
                parameters: [scopeQueryParameter],
                responses: { '200': { description: 'OK' } },
            },
        },
        '/api/v1/capacity/resources': {
            get: {
                summary: 'Tempo resources and availability',
                parameters: [scopeQueryParameter],
                responses: { '200': { description: 'OK' } },
            },
        },
        '/api/v1/capacity/overview': {
            get: {
                summary: 'Weekly capacity with official and raw provisional bookings',
                parameters: [scopeQueryParameter],
                responses: { '200': { description: 'Capacity overview' } },
            },
        },
        '/api/v1/capacity/calendar': {
            get: {
                summary: 'Daily capacity consolidated by day, ISO week, organization sprint, or month',
                parameters: [scopeQueryParameter],
                responses: {
                    '200': {
                        description: 'Capacity calendar',
                        content: {
                            'application/json': {
                                schema: {
                                    type: 'object',
                                    properties: { data: { $ref: '#/components/schemas/CapacityCalendar' } },
                                    required: ['data'],
                                },
                            },
                        },
                    },
                },
            },
        },
        '/api/v1/capacity/calendar-allocations': {
            post: {
                summary: 'Create an exact-date allocation in total hours or capacity percentage',
                responses: { '201': { description: 'Created' } },
            },
        },
        '/api/v1/capacity/calendar-allocations/bulk-delete': {
            post: {
                summary: 'Atomically delete selected manual allocation periods with reason and version checks',
                requestBody: jsonBody('DailyAllocationBulkDelete'),
                responses: {
                    '204': { description: 'Deleted' },
                    '409': { description: 'Stale selection; no changes applied' },
                },
            },
        },
        '/api/v1/capacity/calendar-allocations/{id}/reschedule': {
            post: {
                summary: 'Move a selected period or entire manual series without changing other periods',
                parameters: [pathId('id')],
                requestBody: jsonBody('DailyAllocationReschedule'),
                responses: {
                    '200': { description: 'Updated' },
                    '409': { description: 'Date or version conflict' },
                },
            },
        },
        '/api/v1/workspace/saved-views/{id}': {
            patch: {
                summary: 'Rename an owned saved view or update its filters; existing duplicate names retain their identities',
                parameters: [pathId('id')],
                responses: {
                    '200': { description: 'Updated' },
                    '404': { description: 'Not found or not owned' },
                },
            },
            delete: {
                summary: 'Delete an owned saved view',
                parameters: [pathId('id')],
                responses: { '204': { description: 'Deleted' } },
            },
        },
        '/api/v1/capacity/calendar-allocations/{id}': {
            patch: {
                summary: 'Edit or reassign a manual allocation over an exact date range',
                responses: { '200': { description: 'Updated' } },
            },
            delete: {
                summary: 'Delete a period or archive an entire manual daily allocation with a reason',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/AllocationDeleteInput' },
                        },
                    },
                },
                responses: { '204': { description: 'Deleted' } },
            },
        },
        '/api/v1/capacity/calendar-allocations/{id}/staff': {
            post: {
                summary: 'Partially staff an expertise placeholder over an exact date range',
                responses: { '201': { description: 'Staffed' } },
            },
        },
        '/api/v1/capacity/allocations': {
            post: {
                summary: 'Create a recurring weekly allocation',
                responses: { '201': { description: 'Created' }, '403': { description: 'Forbidden' } },
            },
        },
        '/api/v1/capacity/allocations/{id}': {
            patch: {
                summary: 'Edit or reassign one week, future weeks, or an entire allocation series',
                responses: { '200': { description: 'Updated' } },
            },
            delete: {
                summary: 'Archive an allocation series with a reason',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/AllocationDeleteInput' },
                        },
                    },
                },
                responses: { '204': { description: 'Archived' } },
            },
        },
        '/api/v1/capacity/allocations/{id}/staff': {
            post: {
                summary: 'Staff a placeholder allocation from an effective week',
                responses: { '200': { description: 'Staffed' } },
            },
        },
        '/api/v1/capacity/exceptions': {
            post: {
                summary: 'Create a local capacity exception',
                responses: { '201': { description: 'Created' }, '403': { description: 'Forbidden' } },
            },
        },
        '/api/v1/budgets': {
            get: { summary: 'Budget portfolio', responses: { '200': { description: 'OK' } } },
        },
        '/api/v1/budgets/projects/{projectId}/dashboard': {
            get: {
                summary: 'Shared Tempo/Jira actuals and EDT plus selected active DDC references',
                parameters: [
                    pathId('projectId'),
                    {
                        name: 'ddc',
                        in: 'query',
                        schema: { type: 'array', items: { type: 'string', format: 'uuid' } },
                    },
                ],
                responses: { '200': jsonResponse('BudgetDashboardResponse') },
            },
        },
        '/api/v1/budgets/projects/{projectId}/phases': {
            get: {
                summary: 'Project phase configuration and mappings',
                parameters: [pathId('projectId')],
                responses: { '200': { description: 'OK' } },
            },
            post: {
                summary: 'Create an audited budget phase',
                parameters: [pathId('projectId')],
                requestBody: jsonBody('BudgetPhaseInput'),
                responses: { '201': { description: 'Created' } },
            },
        },
        '/api/v1/budgets/projects/{projectId}/phases/{phaseId}': {
            patch: {
                summary: 'Update a budget phase with optimistic concurrency',
                parameters: [pathId('projectId'), pathId('phaseId')],
                requestBody: jsonBody('BudgetPhaseUpdate'),
                responses: { '200': { description: 'Updated' }, '409': { description: 'Stale version' } },
            },
            delete: {
                summary: 'Delete a phase and unclassify its mappings atomically',
                parameters: [pathId('projectId'), pathId('phaseId')],
                requestBody: jsonBody('BudgetVersion'),
                responses: { '200': { description: 'Deleted' }, '409': { description: 'Stale version' } },
            },
        },
        '/api/v1/budgets/projects/{projectId}/mappings/{workItemId}': {
            put: {
                summary: 'Assign a ticket or epic to a phase in the same project',
                parameters: [pathId('projectId'), pathId('workItemId')],
                requestBody: jsonBody('BudgetPhaseMappingInput'),
                responses: { '200': { description: 'Updated' }, '409': { description: 'Stale version' } },
            },
        },
        '/api/v1/budgets/envelopes': {
            post: {
                summary: 'Create a project budget envelope',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': { schema: { $ref: '#/components/schemas/BudgetEnvelopeInput' } },
                    },
                },
                responses: { '201': { description: 'Created' }, '409': { description: 'Conflict' } },
            },
        },
        '/api/v1/budgets/envelopes/{id}': {
            patch: {
                summary: 'Update a project budget envelope',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/BudgetEnvelopeUpdateInput' },
                        },
                    },
                },
                responses: { '200': { description: 'Updated' }, '404': { description: 'Not found' } },
            },
        },
        '/api/v1/budgets/envelopes/{id}/restore': {
            post: {
                summary: 'Restore an archived project budget envelope',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/BudgetEnvelopeRestoreInput' },
                        },
                    },
                },
                responses: {
                    '200': { description: 'Restored' },
                    '404': { description: 'Not found' },
                    '409': { description: 'Envelope changed concurrently' },
                },
            },
        },
        '/api/v1/projects': {
            get: {
                summary: 'Project portfolio',
                parameters: [scopeQueryParameter, projectLifecycleQueryParameter],
                responses: { '200': { description: 'OK' } },
            },
            post: { summary: 'Create a project', responses: { '201': { description: 'Created' } } },
        },
        '/api/v1/projects/{projectId}/contract': {
            patch: {
                summary: 'Update audited local project contract values',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': { schema: { $ref: '#/components/schemas/ProjectContractInput' } },
                    },
                },
                responses: { '200': { description: 'Updated' }, '422': { description: 'Invalid period' } },
            },
        },
        '/api/v1/projects/{projectId}/forecast-settings': {
            patch: {
                summary: 'Update project forecast settings and audited remaining adjustment',
                responses: { '200': { description: 'Updated' } },
            },
        },
        '/api/v1/projects/{projectId}/insights': {
            get: {
                summary: 'Project delivery and financial forecast',
                responses: { '200': { description: 'Forecast insight' } },
            },
        },
        '/api/v1/projects/{projectId}/insights/history': {
            get: {
                summary: 'Daily forecast history',
                responses: { '200': { description: 'Forecast history' } },
            },
        },
        '/api/v1/insights/portfolio': {
            get: {
                summary: 'Portfolio delivery, capacity, and financial insights',
                parameters: [
                    scopeQueryParameter,
                    {
                        name: 'currency',
                        in: 'query',
                        required: false,
                        schema: { type: 'string', enum: ['CAD', 'USD'], default: 'CAD' },
                    },
                ],
                responses: {
                    '200': {
                        description: 'Portfolio insights',
                        content: {
                            'application/json': {
                                schema: {
                                    type: 'object',
                                    properties: { data: { $ref: '#/components/schemas/PortfolioInsights' } },
                                    required: ['data'],
                                },
                            },
                        },
                    },
                    '400': { description: 'Invalid query parameters' },
                    '403': { description: 'Forbidden' },
                },
            },
        },
        '/api/v1/planning/unplanned': {
            get: { summary: 'Unplanned Jira work items', responses: { '200': { description: 'OK' } } },
        },
        '/api/v1/planning/allocations': {
            get: {
                summary: 'Read-only Jira project demand',
                responses: { '200': { description: 'OK' } },
            },
        },
        '/api/v1/billing/time-entries': {
            get: {
                summary: 'Tempo billing review queue',
                parameters: [
                    ...blockedWorklogFilters,
                    { name: 'blocked', in: 'query', schema: { type: 'string', enum: ['true', 'false'] } },
                    { name: 'page', in: 'query', schema: { type: 'integer', minimum: 1 } },
                    { name: 'pageSize', in: 'query', schema: { type: 'integer', minimum: 1, maximum: 100 } },
                ],
                responses: { '200': { description: 'OK' } },
            },
        },
        '/api/v1/billing/projects': {
            get: {
                summary: 'Projects available for billing review',
                responses: { '200': { description: 'OK' } },
            },
        },
        '/api/v1/billing/period-preview': {
            get: {
                summary: 'Preview Tempo Billable time consolidated by role and epic',
                responses: { '200': { description: 'Billing period preview' } },
            },
        },
        '/api/v1/billing/validation-issues': {
            get: {
                summary: 'List daily Tempo and Jira worklog validation issues',
                responses: { '200': { description: 'Validation issues' } },
            },
        },
        '/api/v1/billing/validation-issues/{id}': {
            patch: {
                summary: 'Ignore or reopen an audited billing validation issue',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/BillingValidationDecision' },
                        },
                    },
                },
                responses: { '200': { description: 'Updated' } },
            },
        },
        '/api/v1/billing/validation-issue-groups': {
            get: {
                summary: 'List Jira/Tempo validation issues grouped by project, kind, and Jira issue',
                responses: { '200': { description: 'Grouped validation issues' } },
            },
        },
        '/api/v1/billing/validation-issue-groups/decision': {
            post: {
                summary: 'Ignore or reopen every validation issue in a displayed group',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/BillingValidationGroupDecision' },
                        },
                    },
                },
                responses: { '200': { description: 'Updated group' } },
            },
        },
        '/api/v1/billing/batches/from-period': {
            post: {
                summary: 'Create an idempotent role-level billing batch from a server-side period preview',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/CreatePeriodBillingBatch' },
                        },
                    },
                },
                responses: { '201': { description: 'Created' }, '409': { description: 'Blocked' } },
            },
        },
        '/api/v1/billing/deposits': {
            get: {
                summary: 'List project prepaid credits separately from budget envelopes',
                responses: { '200': { description: 'Project deposits' } },
            },
        },
        '/api/v1/billing/deposits/{projectId}': {
            put: {
                summary: 'Create or update the audited initial prepaid credit for a project',
                responses: { '200': { description: 'Updated' }, '201': { description: 'Created' } },
            },
        },
        '/api/v1/billing/deposits/{projectId}/applications': {
            post: {
                summary: 'Apply an idempotent manual credit against a project deposit',
                responses: { '201': { description: 'Applied' }, '409': { description: 'Overapplied' } },
            },
        },
        '/api/v1/governance/risks': {
            get: { summary: 'Project risks', responses: { '200': { description: 'OK' } } },
            post: { summary: 'Create a risk', responses: { '201': { description: 'Created' } } },
        },
        '/api/v1/governance/decisions': {
            get: { summary: 'Project decisions', responses: { '200': { description: 'OK' } } },
            post: { summary: 'Create a decision', responses: { '201': { description: 'Created' } } },
        },
        '/api/v1/governance/change-requests': {
            get: { summary: 'Project change requests', responses: { '200': { description: 'OK' } } },
            post: {
                summary: 'Create a change request',
                responses: { '201': { description: 'Created' } },
            },
        },
        '/api/v1/reports/projects': {
            get: {
                summary: 'List tracked projects available to the report workflow',
                responses: { '200': { description: 'Report projects' } },
            },
        },
        '/api/v1/reports/projects/{projectId}/preview': {
            get: {
                summary: 'Preview explicitly client-visible project information',
                responses: { '200': { description: 'Publication preview' } },
            },
        },
        '/api/v1/reports/projects/{projectId}/baselines': {
            get: {
                summary: 'List immutable report baselines',
                responses: { '200': { description: 'Report baselines' } },
            },
            post: {
                summary: 'Create an immutable report baseline from the current preview',
                responses: { '201': { description: 'Baseline created' } },
            },
        },
        '/api/v1/reports/baselines/{id}/approve': {
            post: {
                summary: 'Approve a report baseline for publication',
                responses: { '200': { description: 'Baseline approved' } },
            },
        },
        '/api/v1/reports/baselines/{id}/publish': {
            post: {
                summary: 'Publish an approved report baseline to Scope',
                responses: { '200': { description: 'Baseline published' } },
            },
        },
        '/api/v1/reports/baselines/{id}.pdf': {
            get: {
                summary: 'Render a report baseline as a private PDF',
                responses: { '200': { description: 'Baseline PDF' } },
            },
        },
        '/api/v1/integrations': {
            get: { summary: 'Integration status', responses: { '200': { description: 'OK' } } },
        },
        '/api/v1/integrations/sync-all': {
            post: {
                summary: 'Queue all configured providers for synchronization',
                responses: { '202': { description: 'Queued' }, '403': { description: 'Forbidden' } },
            },
        },
        '/api/v1/settings/mappings/jira': {
            get: {
                summary: 'Jira project and issue mappings',
                responses: { '200': { description: 'OK' } },
            },
            patch: {
                summary: 'Resolve a Jira mapping',
                responses: { '200': { description: 'Updated' } },
            },
        },
        '/api/v1/settings/mappings/hubspot': {
            get: {
                summary: 'HubSpot deal staging mappings',
                responses: { '200': { description: 'OK' } },
            },
            patch: { summary: 'Map a HubSpot deal', responses: { '200': { description: 'Updated' } } },
        },
        '/api/v1/settings/jira-fields': {
            get: { summary: 'Jira custom field discovery', responses: { '200': { description: 'OK' } } },
            patch: {
                summary: 'Configure Jira custom fields',
                responses: { '200': { description: 'Updated' } },
            },
        },
        '/api/v1/settings/tempo-plans': {
            get: {
                summary: 'Tempo absence plan configuration',
                responses: {
                    '200': {
                        description: 'OK',
                        content: {
                            'application/json': {
                                schema: {
                                    type: 'object',
                                    required: ['data'],
                                    properties: {
                                        data: {
                                            allOf: [
                                                { $ref: '#/components/schemas/TempoAbsenceMapping' },
                                                {
                                                    type: 'object',
                                                    properties: { available: { type: ['boolean', 'null'] } },
                                                },
                                            ],
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
            patch: {
                summary: 'Configure Tempo absence plan items',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/TempoAbsenceMapping' },
                        },
                    },
                },
                responses: { '200': { description: 'Updated' } },
            },
        },
        '/api/v1/settings/planning': {
            get: {
                summary: 'Organization capacity and forecast defaults',
                responses: { '200': { description: 'Settings' } },
            },
            patch: {
                summary: 'Update organization capacity and forecast defaults',
                responses: { '200': { description: 'Updated' } },
            },
        },
        '/api/v1/settings/expertises': {
            get: {
                summary: 'List the expertise catalog',
                responses: { '200': { description: 'Expertises' } },
            },
            post: {
                summary: 'Create an expertise',
                responses: { '201': { description: 'Created' } },
            },
        },
        '/api/v1/settings/bill-rates': {
            get: {
                summary: 'List project and role client bill rates stored in PostgreSQL',
                responses: { '200': { description: 'Bill rates' } },
            },
            post: {
                summary: 'Create or update a project bill rate',
                responses: { '201': { description: 'Stored' } },
            },
        },
        '/api/v1/settings/bill-rate-diagnostics': {
            get: {
                summary: 'Missing bill rates for a project and work-date range',
                parameters: [
                    {
                        name: 'projectId',
                        in: 'query',
                        required: true,
                        schema: { type: 'string', format: 'uuid' },
                    },
                    { name: 'from', in: 'query', required: true, schema: { type: 'string', format: 'date' } },
                    { name: 'to', in: 'query', required: true, schema: { type: 'string', format: 'date' } },
                ],
                responses: {
                    '200': { description: 'Missing rate roles and affected entry date ranges' },
                    '400': { description: 'Invalid date range' },
                    '403': { description: 'Forbidden' },
                    '404': { description: 'Project not found or inaccessible' },
                },
            },
        },
        '/api/v1/settings/capacity-value-rate': {
            get: {
                summary: 'Read the organization average capacity value rate',
                responses: {
                    '200': {
                        description: 'Capacity value rate',
                        content: {
                            'application/json': {
                                schema: {
                                    type: 'object',
                                    properties: { data: { $ref: '#/components/schemas/CapacityValueRate' } },
                                    required: ['data'],
                                },
                            },
                        },
                    },
                    '403': { description: 'Forbidden' },
                },
            },
            patch: {
                summary: 'Configure or clear the organization average capacity value rate',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/CapacityValueRateInput' },
                        },
                    },
                },
                responses: {
                    '200': { description: 'Capacity value rate updated' },
                    '400': { description: 'Invalid rate' },
                    '403': { description: 'Forbidden' },
                },
            },
        },
        '/api/v1/settings/standard-rates': {
            get: {
                summary: 'List effective organization standard bill rates by expertise',
                responses: { '200': { description: 'Standard rates' } },
            },
            post: {
                summary: 'Create or update a standard bill rate',
                responses: { '201': { description: 'Stored' } },
            },
        },
        '/api/v1/settings/bill-rates/import/preview': {
            post: {
                summary: 'Validate a canonical CSV import and request mappings to catalogued Tempo roles, including unstaffed roles; repeated Tempo mappings preserve distinct source-specific local rate roles, and project currency confirmations are requested when needed',
                responses: {
                    '200': {
                        description: 'Role mappings or project currency confirmation required, or idempotent preview replayed',
                    },
                    '201': { description: 'Previewed' },
                },
            },
        },
        '/api/v1/settings/bill-rates/import/{id}/confirm': {
            post: {
                summary: 'Confirm a previously validated rate import',
                responses: { '200': { description: 'Confirmed' } },
            },
        },
        '/api/v1/settings/budget-imports/preview': {
            post: {
                summary: 'Validate a complete budget and prepaid-credit CSV snapshot',
                responses: {
                    '200': { description: 'Idempotent preview replayed' },
                    '201': { description: 'Previewed' },
                },
            },
        },
        '/api/v1/settings/budget-imports/{id}/confirm': {
            post: {
                summary: 'Atomically confirm a resolved budget CSV snapshot',
                responses: {
                    '200': { description: 'Confirmed' },
                    '409': { description: 'Unresolved issues' },
                },
            },
        },
        '/api/v1/financial-forecast': {
            get: {
                summary: 'Monthly official and raw provisional revenue forecast in CAD',
                responses: { '200': { description: 'Financial forecast' } },
            },
        },
        '/api/v1/agent/conversations': {
            get: {
                summary: 'List retained Forecast Agent conversations',
                responses: { '200': { description: 'Conversations' } },
            },
        },
        '/api/v1/agent/conversations/{id}/messages': {
            get: {
                summary: 'Read the authenticated user’s retained conversation',
                responses: { '200': { description: 'Messages' }, '404': { description: 'Not found' } },
            },
        },
        '/api/v1/agent/messages': {
            post: {
                summary: 'Ask the read-only Forecast Agent',
                responses: {
                    '200': { description: 'Assistant answer' },
                    '503': { description: 'Disabled' },
                },
            },
        },
        '/api/v1/settings/clients': {
            get: {
                summary: 'Tempo client tracking settings',
                responses: { '200': { description: 'OK' } },
            },
        },
        '/api/v1/settings/clients/{id}/tracking': {
            patch: {
                summary: 'Update Tempo client tracking',
                responses: { '200': { description: 'Updated' }, '403': { description: 'Forbidden' } },
            },
        },
        '/api/v1/settings/projects/{id}/tracking': {
            patch: {
                summary: 'Update Tempo project tracking',
                responses: { '200': { description: 'Updated' }, '403': { description: 'Forbidden' } },
            },
        },
        '/api/v1/settings/projects/{id}/signature': {
            patch: {
                summary: 'Update the audited local project signature status',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': { schema: { $ref: '#/components/schemas/ProjectSignatureInput' } },
                    },
                },
                responses: {
                    '200': { description: 'Updated project signature status' },
                    '403': { description: 'Forbidden' },
                    '404': { description: 'Project not found' },
                },
            },
        },
        '/api/v1/settings/projects/{id}/billing': {
            get: {
                summary: 'Read the consolidated-time epic override and eligible Jira epics',
                parameters: [
                    {
                        name: 'q',
                        in: 'query',
                        required: false,
                        schema: { type: 'string', minLength: 2, maxLength: 200 },
                    },
                    {
                        name: 'limit',
                        in: 'query',
                        required: false,
                        schema: { type: 'integer', minimum: 1, maximum: 50, default: 50 },
                    },
                ],
                responses: { '200': { description: 'Project billing settings' } },
            },
            patch: {
                summary: 'Configure or clear the consolidated-time epic override',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/ProjectBillingSettingsInput' },
                        },
                    },
                },
                responses: {
                    '200': { description: 'Updated project billing settings' },
                    '404': { description: 'Project or configured epic not found' },
                },
            },
        },
        '/api/v1/settings/contracts': {
            get: {
                summary: 'Read organization contract warning thresholds',
                responses: { '200': { description: 'Contract settings' } },
            },
            patch: {
                summary: 'Update audited organization contract warning thresholds',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': { schema: { $ref: '#/components/schemas/ContractSettings' } },
                    },
                },
                responses: { '200': { description: 'Updated contract settings' } },
            },
        },
    },
    components: {
        schemas: {
            AllocationDeletionsResponse: z.toJSONSchema(allocationDeletionsResponseSchema),
            AllocationRestoreResponse: z.toJSONSchema(allocationRestoreResponseSchema),
            SquadPlanningPeriodsResponse: z.toJSONSchema(squadPlanningPeriodsResponseSchema),
            BlockedWorklogsSummaryResponse: z.toJSONSchema(blockedWorklogsSummaryResponseSchema),
            DataQualityIssuesResponse: z.toJSONSchema(dataQualityIssuesResponseSchema),
            DataQualityOwnersResponse: z.toJSONSchema(dataQualityOwnersResponseSchema),
            ResourcesResponse: z.toJSONSchema(resourcesResponseSchema),
            ResourceProfileResponse: z.toJSONSchema(resourceProfileResponseSchema),
            TempoTeamsResponse: z.toJSONSchema(tempoTeamsResponseSchema),
            ResourceSources: z.toJSONSchema(resourceSourcesSchema),
            TempoAbsenceMapping: z.toJSONSchema(tempoAbsenceMappingSchema),
            TempoWorklogAbsencesSettingsResponse: z.toJSONSchema(tempoWorklogAbsencesSettingsResponseSchema),
            ExchangeRateRange: z.toJSONSchema(exchangeRateRangeSchema),
            ExchangeRateCoverageResponse: z.toJSONSchema(exchangeRateCoverageResponseSchema),
            ExchangeRateResyncResponse: z.toJSONSchema(exchangeRateResyncResponseSchema),
            Money: z.toJSONSchema(moneySchema),
            Project: z.toJSONSchema(projectSchema),
            ProjectSignatureInput: z.toJSONSchema(projectSignatureInputSchema),
            ProjectContractStatus: z.toJSONSchema(projectContractStatusSchema),
            CapacityPoint: z.toJSONSchema(capacityPointSchema),
            BudgetSummary: z.toJSONSchema(budgetSummarySchema),
            BudgetEnvelopeInput: z.toJSONSchema(budgetEnvelopeInputSchema),
            BudgetEnvelopeUpdateInput: z.toJSONSchema(budgetEnvelopeUpdateInputSchema),
            BudgetEnvelopeRestoreInput: z.toJSONSchema(budgetEnvelopeRestoreInputSchema),
            ProjectContractInput: z.toJSONSchema(projectContractInputSchema),
            ProjectStateInput: z.toJSONSchema(projectStateInputSchema),
            ProjectStateResponse: z.toJSONSchema(projectStateResponseSchema),
            SquadsResponse: z.toJSONSchema(squadsResponseSchema),
            SquadOptionsResponse: z.toJSONSchema(squadOptionsResponseSchema),
            SquadCreate: z.toJSONSchema(squadCreateSchema),
            SquadAdminUpdate: z.toJSONSchema(squadAdminUpdateSchema),
            SquadMembershipUpdate: z.toJSONSchema(squadMembershipUpdateSchema),
            SquadSprintInput: z.toJSONSchema(squadSprintInputSchema),
            SquadSprintUpdate: z.toJSONSchema(squadSprintUpdateSchema),
            SquadSeedInput: z.toJSONSchema(squadSeedInputSchema),
            BudgetDashboardResponse: z.toJSONSchema(budgetDashboardResponseSchema),
            BudgetPhaseInput: z.toJSONSchema(budgetPhaseInputSchema),
            BudgetPhaseUpdate: z.toJSONSchema(budgetPhaseUpdateSchema),
            BudgetPhaseMappingInput: z.toJSONSchema(budgetPhaseMappingInputSchema),
            BudgetVersion: z.toJSONSchema(budgetVersionSchema),
            ProjectContractSummary: z.toJSONSchema(projectContractSummarySchema),
            ProjectHistoryEntry: z.toJSONSchema(projectHistoryEntrySchema),
            ContractSettings: z.toJSONSchema(contractSettingsSchema),
            BudgetImportInput: z.toJSONSchema(budgetImportInputSchema),
            BudgetImportRun: z.toJSONSchema(budgetImportRunSchema),
            ProjectDepositInput: z.toJSONSchema(projectDepositInputSchema),
            DepositApplicationInput: z.toJSONSchema(depositApplicationInputSchema),
            ProjectDepositSummary: z.toJSONSchema(projectDepositSummarySchema),
            BillingPeriodPreview: z.toJSONSchema(billingPeriodPreviewSchema),
            BillingValidationDecision: z.toJSONSchema(billingValidationDecisionSchema),
            BillingValidationGroup: z.toJSONSchema(billingValidationGroupSchema),
            BillingValidationGroupDecision: z.toJSONSchema(billingValidationGroupDecisionSchema),
            ProjectBillingSettings: z.toJSONSchema(projectBillingSettingsSchema),
            ProjectBillingSettingsInput: z.toJSONSchema(projectBillingSettingsInputSchema),
            CreatePeriodBillingBatch: z.toJSONSchema(createPeriodBillingBatchSchema),
            RiskInput: z.toJSONSchema(riskInputSchema),
            DecisionInput: z.toJSONSchema(decisionInputSchema),
            NoteInput: z.toJSONSchema(noteInputSchema),
            PublicationSnapshot: z.toJSONSchema(publicationSnapshotSchema),
            ReportProject: z.toJSONSchema(reportProjectSchema),
            ReportBaseline: z.toJSONSchema(reportBaselineSchema),
            ClientTrackingInput: z.toJSONSchema(clientTrackingInputSchema),
            ProjectTrackingInput: z.toJSONSchema(projectTrackingInputSchema),
            PlanningSettings: z.toJSONSchema(planningSettingsSchema),
            CanonicalRateImportInput: z.toJSONSchema(canonicalRateImportInputSchema),
            ProjectSoldAverageRateOverrideInput: z.toJSONSchema(projectSoldAverageRateOverrideInputSchema),
            Expertise: z.toJSONSchema(expertiseSchema),
            AllocationInput: z.toJSONSchema(allocationInputSchema),
            AllocationDeleteInput: z.toJSONSchema(allocationDeleteInputSchema),
            AllocationUpdate: z.toJSONSchema(allocationUpdateSchema),
            CapacityOverview: z.toJSONSchema(capacityOverviewSchema),
            DailyAllocationInput: z.toJSONSchema(dailyAllocationInputSchema),
            DailyAllocationStaffingInput: z.toJSONSchema(dailyAllocationStaffingInputSchema),
            DailyAllocationUpdate: z.toJSONSchema(dailyAllocationUpdateSchema),
            DailyAllocationBulkDelete: z.toJSONSchema(dailyAllocationBulkDeleteSchema),
            DailyAllocationReschedule: z.toJSONSchema(dailyAllocationRescheduleSchema),
            CapacityCalendar: z.toJSONSchema(capacityCalendarSchema),
            CapacityValueRate: z.toJSONSchema(capacityValueRateSchema),
            CapacityValueRateInput: z.toJSONSchema(capacityValueRateInputSchema),
            StandardRateInput: z.toJSONSchema(standardRateInputSchema),
            FinancialForecast: z.toJSONSchema(financialForecastSchema),
            ProjectInsight: z.toJSONSchema(projectInsightSchema),
            ProjectForecastSettingsInput: z.toJSONSchema(projectForecastSettingsInputSchema),
            PortfolioInsights: z.toJSONSchema(portfolioInsightsSchema),
            AgentMessageInput: z.toJSONSchema(agentMessageInputSchema),
        },
    },
};
//# sourceMappingURL=openapi.js.map